import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Search, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Insights = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog?limit=20`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      // Fallback data
      setPosts([
        {
          id: '1',
          title: 'The Rise of Second Homes in Maharashtra Hill Stations',
          excerpt:
            'Exploring the growing trend of luxury second homes in Lonavala and Khandala as HNIs seek weekend sanctuaries away from urban chaos.',
          category: 'Market Insights',
          author: 'Venara Estate',
          created_at: new Date().toISOString(),
          image_url: 'https://images.unsplash.com/photo-1644620426342-471c3ed4e1d4?crop=entropy&cs=srgb&fm=jpg&q=85',
        },
        {
          id: '2',
          title: 'Dubai Real Estate: A Guide for Indian Investors',
          excerpt:
            'Understanding investment opportunities, legal frameworks, and tax implications for Indian HNWIs investing in Dubai luxury properties.',
          category: 'International Markets',
          author: 'Venara Estate',
          created_at: new Date().toISOString(),
          image_url: 'https://images.unsplash.com/photo-1748373448914-1d7f882700e2?crop=entropy&cs=srgb&fm=jpg&q=85',
        },
        {
          id: '3',
          title: 'Alibaug: The New Riviera for Mumbai Elite',
          excerpt:
            'How Alibaug transformed from a weekend getaway to a premier destination for luxury villas and beachfront estates.',
          category: 'Location Spotlight',
          author: 'Venara Estate',
          created_at: new Date().toISOString(),
          image_url: 'https://images.unsplash.com/photo-1728486888025-34e8d66b6b7b?crop=entropy&cs=srgb&fm=jpg&q=85',
        },
        {
          id: '4',
          title: 'Understanding RERA: A Buyer\'s Guide',
          excerpt:
            'Everything you need to know about RERA regulations and how they protect luxury property buyers in India.',
          category: 'Legal & Compliance',
          author: 'Venara Estate',
          created_at: new Date().toISOString(),
          image_url: 'https://images.unsplash.com/photo-1666289158111-7576ce2ccfae?crop=entropy&cs=srgb&fm=jpg&q=85',
        },
        {
          id: '5',
          title: 'Luxury Real Estate Trends 2025',
          excerpt:
            'Key trends shaping the luxury real estate market in India—from smart homes to sustainable architecture.',
          category: 'Market Insights',
          author: 'Venara Estate',
          created_at: new Date().toISOString(),
          image_url: 'https://images.unsplash.com/photo-1760570317619-0bad6e3dd4f1?crop=entropy&cs=srgb&fm=jpg&q=85',
        },
        {
          id: '6',
          title: 'Investment Strategies for NRI Buyers',
          excerpt:
            'A comprehensive guide for Non-Resident Indians looking to invest in Indian luxury real estate.',
          category: 'Investment',
          author: 'Venara Estate',
          created_at: new Date().toISOString(),
          image_url: 'https://images.unsplash.com/photo-1666094383847-9fae5f90a3c6?crop=entropy&cs=srgb&fm=jpg&q=85',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMMM dd, yyyy');
    } catch {
      return 'Recent';
    }
  };

  const filteredPosts = posts.filter(
    (post) =>
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[60vh] w-full overflow-hidden" data-testid="insights-hero">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2, ease: 'easeOut' }}
          className="absolute inset-0"
        >
          <img
            src="https://images.unsplash.com/photo-1666094383847-9fae5f90a3c6?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Market Insights"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary/60" />
        </motion.div>

        <div className="relative h-full flex items-center justify-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center max-w-4xl"
          >
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground tracking-tight mb-4">
              Market Insights
            </h1>
            <p className="text-primary-foreground/90 text-lg">
              Expert perspectives on luxury real estate trends, investment strategies, and market intelligence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-12 px-6 md:px-12 lg:px-24 bg-accent">
        <div className="max-w-7xl mx-auto">
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
            <input
              type="text"
              placeholder="Search insights..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="insights-search-input"
              className="w-full pl-12 pr-4 py-4 bg-white border border-muted focus:border-secondary focus:outline-none font-manrope"
            />
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-background" data-testid="insights-grid">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center">
              <p className="text-muted-foreground">Loading insights...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center">
              <p className="text-muted-foreground">No insights found matching your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  data-testid={`insight-post-${index}`}
                  className="group bg-white border border-muted hover:border-secondary transition-all duration-500 overflow-hidden hover:shadow-[0_20px_40px_-4px_rgba(15,40,30,0.2)]"
                >
                  {/* Image */}
                  <div className="relative overflow-hidden h-64">
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-secondary text-secondary-foreground px-3 py-1 text-xs font-medium uppercase">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-3">
                      <Calendar size={14} />
                      <span>{formatDate(post.created_at)}</span>
                    </div>

                    <h3 className="font-playfair text-xl font-semibold text-primary mb-3 group-hover:text-secondary transition-colors duration-300">
                      {post.title}
                    </h3>

                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">{post.excerpt}</p>

                    <div className="flex items-center gap-2 text-secondary text-sm font-medium group-hover:gap-4 transition-all duration-300">
                      Read More
                      <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Insights;
